import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { View } from 'api/views/types';
import { Filter } from 'components/molecules/filters/types';
import { defaultFilter } from 'components/molecules/filters/utils';
import { DateRange } from 'components/organisms/projects-report/common/range-picker/types';
import { FilterableField } from 'components/organisms/tasks/fields';
import { useSearch } from 'hooks/use-search';
import { Content } from 'stores/core';
import { FieldFilter } from '../../tasks/filters';
import { getTimesheetOverallReportSearchParamTransformer } from './overall-search-param-transformer';
import { TimesheetOverallReportSearchParams } from './types';
import { TimesheetReportViewDefinition } from './views';

interface ReturnProps {
  selectSearchParams: (next: TimesheetOverallReportSearchParams) => void;
  replaceSearchParams: (next: TimesheetOverallReportSearchParams) => void;
  clearFilters: () => void;
  onFilterChange: (nextFilter: Filter) => void;
  onViewSelect: (nextView: View<TimesheetReportViewDefinition>) => void;
  onRangeChange: (nextRange: DateRange) => void;
  searchParams: TimesheetOverallReportSearchParams;
  lookup: Record<string, FilterableField>;
}

export const useOverallReportSearchParams = (
  lookup: Record<string, FilterableField>,
  defaultRange: DateRange,
  status: Content<any>[0],
): ReturnProps => {
  const transformer = useMemo(
    () => getTimesheetOverallReportSearchParamTransformer(lookup, defaultRange),
    [lookup, defaultRange],
  );
  const [search, setSearch] = useSearch(transformer);
  const [pageSearchParams, setPageSearchParams] = useState(search);
  const selectSearchParams = useCallback(
    (params: TimesheetOverallReportSearchParams) => {
      if (status === 'loading') {
        return;
      }

      setPageSearchParams(params);
      setSearch(params);
    },
    [status, setPageSearchParams, setSearch],
  );
  const replaceSearchParams = useCallback(
    (params: TimesheetOverallReportSearchParams) => {
      setPageSearchParams(params);
      setSearch(params, true);
    },
    [setPageSearchParams, setSearch],
  );
  const clearFilters = useCallback(() => {
    selectSearchParams({
      viewId: pageSearchParams.viewId,
      filter: {
        ...defaultFilter,
        range: pageSearchParams.filter.range,
      },
    });
  }, [selectSearchParams, pageSearchParams]);

  useEffect(() => {
    if (
      !isEqual(search, {
        ...pageSearchParams,
        filter: {
          ...pageSearchParams.filter,
          criteria: pageSearchParams.filter.criteria.filter(
            FieldFilter.isValid,
          ),
        },
      })
    ) {
      setPageSearchParams(search);
    }
  }, [search, pageSearchParams, setPageSearchParams]);

  const onFilterChange = useCallback(
    (filter: Filter) => {
      selectSearchParams({
        ...pageSearchParams,
        filter: {
          ...pageSearchParams.filter,
          ...filter,
        },
      });
    },
    [pageSearchParams, selectSearchParams],
  );
  const onViewSelect = useCallback(
    ({
      viewId,
      viewDefinition: { filter: nextFilter },
    }: View<TimesheetReportViewDefinition>) => {
      selectSearchParams({
        viewId,
        filter: {
          ...pageSearchParams.filter,
          ...nextFilter,
        },
      });
    },
    [pageSearchParams.filter, selectSearchParams],
  );
  const onRangeChange = useCallback(
    (nextRange: DateRange) => {
      selectSearchParams({
        ...pageSearchParams,
        filter: { ...pageSearchParams.filter, range: nextRange },
      });
    },
    [pageSearchParams, selectSearchParams],
  );

  return {
    selectSearchParams,
    replaceSearchParams,
    clearFilters,
    onFilterChange,
    onViewSelect,
    onRangeChange,
    searchParams: pageSearchParams,
    lookup,
  };
};
